import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Loader2, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { getToken } from '../services/tokenService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api/registres-custom`;
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

const FIELD_TYPES = [
  { value: 'text', label: 'Texte court' },
  { value: 'textarea', label: 'Texte long' },
  { value: 'number', label: 'Nombre' },
  { value: 'date', label: 'Date' },
  { value: 'email', label: 'Email' },
  { value: 'tel', label: 'Téléphone' },
  { value: 'siret', label: 'SIRET' },
  { value: 'select', label: 'Liste de choix' },
  { value: 'boolean', label: 'Oui/Non' },
];

// ---------- Modale création/édition registre ----------
function RegistreFormDialog({ open, onOpenChange, initial, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    nom: '',
    description: '',
    icone: 'BookOpen',
    ged_categorie: '',
    fields: [],
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          nom: initial.nom || '',
          description: initial.description || '',
          icone: initial.icone || 'BookOpen',
          ged_categorie: initial.ged_categorie || '',
          fields: (initial.fields || []).map((f) => ({ ...f, options: (f.options || []).join(', ') })),
        });
      } else {
        setForm({ nom: '', description: '', icone: 'BookOpen', ged_categorie: '', fields: [] });
      }
    }
  }, [open, initial]);

  const addField = () => {
    setForm((s) => ({
      ...s,
      fields: [...s.fields, { key: '', label: '', type: 'text', required: false, options: '' }],
    }));
  };
  const removeField = (idx) => {
    setForm((s) => ({ ...s, fields: s.fields.filter((_, i) => i !== idx) }));
  };
  const updateField = (idx, key, val) => {
    setForm((s) => ({
      ...s,
      fields: s.fields.map((f, i) => (i === idx ? { ...f, [key]: val } : f)),
    }));
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!form.nom.trim()) {
      toast.error('Nom du registre requis');
      return;
    }
    const payload = {
      nom: form.nom.trim(),
      description: form.description?.trim() || null,
      icone: form.icone || 'BookOpen',
      ged_categorie: form.ged_categorie?.trim() || null,
      fields: (form.fields || []).map((f) => ({
        key: (f.key || '').trim() || (f.label || '').trim(),
        label: (f.label || '').trim(),
        type: f.type || 'text',
        required: !!f.required,
        options: f.type === 'select'
          ? (typeof f.options === 'string' ? f.options.split(',').map((s) => s.trim()).filter(Boolean) : f.options)
          : null,
      })),
    };

    // Validation client
    for (const f of payload.fields) {
      if (!f.label) {
        toast.error('Chaque champ doit avoir un libellé');
        return;
      }
      if (f.type === 'select' && (!f.options || f.options.length === 0)) {
        toast.error(`Le champ "${f.label}" est de type Liste mais n'a pas d'options`);
        return;
      }
    }

    setLoading(true);
    try {
      if (isEdit) {
        await axios.patch(`${API}/admin/${initial.id}`, payload, { headers: authHeaders() });
        toast.success('Registre mis à jour');
      } else {
        await axios.post(`${API}/admin`, payload, { headers: authHeaders() });
        toast.success('Registre créé');
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur de sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="registre-custom-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Modifier le registre' : 'Nouveau registre custom'}</DialogTitle>
          <DialogDescription>
            Définissez le nom, la catégorie GED, et le schéma de champs de ce registre.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Nom du registre *</label>
              <Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} required data-testid="registre-nom" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Catégorie GED associée</label>
              <Input
                value={form.ged_categorie}
                onChange={(e) => setForm({ ...form, ged_categorie: e.target.value })}
                placeholder="ex: sinistres, conventions, benevoles"
                data-testid="registre-ged-cat"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-sm font-medium text-gray-700 block mb-1">Description</label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                placeholder="A quoi sert ce registre ?"
              />
            </div>
          </div>

          {/* Schéma des champs */}
          <div className="border rounded-lg p-3 bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-gray-900">Champs du registre</h4>
              <Button type="button" size="sm" variant="outline" onClick={addField} data-testid="add-field-btn">
                <Plus className="w-3.5 h-3.5 mr-1" />
                Ajouter un champ
              </Button>
            </div>
            {form.fields.length === 0 && (
              <p className="text-sm text-gray-500 italic">
                Ajoutez au moins un champ pour définir le contenu des entrées de ce registre.
              </p>
            )}
            <div className="space-y-2">
              {form.fields.map((f, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-start bg-white border rounded-md p-2">
                  <div className="col-span-3">
                    <Input
                      placeholder="Libellé"
                      value={f.label}
                      onChange={(e) => updateField(i, 'label', e.target.value)}
                    />
                  </div>
                  <div className="col-span-3">
                    <Input
                      placeholder="clé technique (auto si vide)"
                      value={f.key}
                      onChange={(e) => updateField(i, 'key', e.target.value)}
                      className="font-mono text-xs"
                    />
                  </div>
                  <div className="col-span-2">
                    <Select value={f.type} onValueChange={(v) => updateField(i, 'type', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FIELD_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3">
                    {f.type === 'select' ? (
                      <Input
                        placeholder="Options séparées par ,"
                        value={f.options}
                        onChange={(e) => updateField(i, 'options', e.target.value)}
                      />
                    ) : (
                      <label className="flex items-center gap-2 text-sm pt-2">
                        <input
                          type="checkbox"
                          checked={f.required}
                          onChange={(e) => updateField(i, 'required', e.target.checked)}
                          className="rounded"
                        />
                        Requis
                      </label>
                    )}
                  </div>
                  <div className="col-span-1 text-right">
                    <Button type="button" size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => removeField(i)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  {f.type === 'select' && (
                    <div className="col-span-12 pl-2">
                      <label className="flex items-center gap-2 text-xs text-gray-600">
                        <input
                          type="checkbox"
                          checked={f.required}
                          onChange={(e) => updateField(i, 'required', e.target.checked)}
                        />
                        Champ requis
                      </label>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Annuler
            </Button>
            <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="registre-save-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {isEdit ? 'Enregistrer' : 'Créer le registre'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Modale création/édition entry ----------
function EntryFormDialog({ open, onOpenChange, registre, initial, onSaved }) {
  const isEdit = !!initial;
  const [fieldsVal, setFieldsVal] = useState({});
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setFieldsVal(initial?.fields || {});
      setNotes(initial?.notes || '');
    }
  }, [open, initial]);

  const handleSave = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    try {
      const payload = { fields: fieldsVal, notes };
      if (isEdit) {
        await axios.patch(`${API}/admin/entries/${initial.id}`, payload, { headers: authHeaders() });
        toast.success('Entrée mise à jour');
      } else {
        await axios.post(`${API}/admin/${registre.id}/entries`, payload, { headers: authHeaders() });
        toast.success('Entrée créée');
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  if (!registre) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="custom-entry-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Modifier l\'entrée' : `Nouvelle entrée — ${registre.nom}`}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSave} className="space-y-3">
          {(registre.fields || []).map((f) => (
            <div key={f.key}>
              <label className="text-sm font-medium text-gray-700 block mb-1">
                {f.label} {f.required && <span className="text-red-500">*</span>}
              </label>
              {f.type === 'textarea' ? (
                <Textarea
                  value={fieldsVal[f.key] || ''}
                  onChange={(e) => setFieldsVal({ ...fieldsVal, [f.key]: e.target.value })}
                  required={f.required}
                />
              ) : f.type === 'select' ? (
                <Select value={fieldsVal[f.key] || ''} onValueChange={(v) => setFieldsVal({ ...fieldsVal, [f.key]: v })}>
                  <SelectTrigger><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent>
                    {(f.options || []).map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : f.type === 'boolean' ? (
                <input
                  type="checkbox"
                  checked={!!fieldsVal[f.key]}
                  onChange={(e) => setFieldsVal({ ...fieldsVal, [f.key]: e.target.checked })}
                  className="ml-2"
                />
              ) : (
                <Input
                  type={['number', 'date', 'email', 'tel'].includes(f.type) ? f.type : 'text'}
                  value={fieldsVal[f.key] || ''}
                  onChange={(e) => setFieldsVal({ ...fieldsVal, [f.key]: e.target.value })}
                  required={f.required}
                />
              )}
            </div>
          ))}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Notes (facultatif)</label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {isEdit ? 'Enregistrer' : 'Créer'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export { RegistreFormDialog, EntryFormDialog };
